/** Ссылка на строку ведомости объёмов: номер строки внутри части РД, подпись
 *  «Раздела» и место в РД с основной надписи листа (`raw.sheet`), текст и адрес
 *  «Скопировать ссылку», разбор якоря `#pos=…&uid=…` при открытии экрана.
 *
 *  Без импортов значений (только `import type`): модуль проверяет `node --test`
 *  (`volumes/logic.test.mjs`), а Node не знает путей-алиасов и расширений `.ts`. */
import type { PtoVolumePosition, PtoVolumeSheet } from '@shared/api';

type Obj = Record<string, unknown>;

function asObj(v: unknown): Obj | null {
  return v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : null;
}

function text(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

// ── Номер строки ──

/** Поля позиции, из которых складывается номер строки. */
export type RowNumbered = Pick<PtoVolumePosition, 'doc_part' | 'row_no' | 'row_no_sub'>;

/** Номер внутри части: «5», «5.1» (вставка после №5), «0.1» (вставка перед первой). */
export function rowNoText(rowNo: number, rowNoSub = 0): string {
  return rowNoSub > 0 ? `${rowNo}.${rowNoSub}` : String(rowNo);
}

/** Метка номера по части и номеру: «СП01 №5», «СП01 №5.1», «ИИ №3». */
export function partRowLabel(part: string, rowNo: number, rowNoSub = 0): string {
  return `${part.trim()} №${rowNoText(rowNo, rowNoSub)}`;
}

/** Метка номера строки (зеркало `row_label` osnova-core); null — строка не
 *  пронумерована или часть не определена. Номер хранится в базе, поэтому фильтр
 *  экрана его не меняет. */
export function rowLabel(p: RowNumbered): string | null {
  const part = p.doc_part?.trim();
  if (!part || typeof p.row_no !== 'number') return null;
  return partRowLabel(part, p.row_no, p.row_no_sub ?? 0);
}

// ── Основная надпись листа и «Раздел» ──

/** Основная надпись листа позиции (`raw.sheet`); null — индексация и «Обновить
 *  разделы» позицию ещё не касались. Поле не того типа — null. */
export function volumeSheet(raw: unknown): PtoVolumeSheet | null {
  const s = asObj(asObj(raw)?.sheet);
  if (!s) return null;
  return {
    code: text(s.code),
    no: typeof s.no === 'number' ? String(s.no) : text(s.no),
    title: text(s.title),
    designation: text(s.designation),
    source: text(s.source),
    mismatch: text(s.mismatch),
    label: text(s.label),
    page: typeof s.page === 'number' ? s.page : null,
    file: text(s.file),
  };
}

/** Подпись «Раздела» «СП01 · Спецификация …» → код части (бейдж) и имя. Подпись
 *  без « · » — имя целиком, без бейджа. */
export function splitSectionLabel(label: string): { code: string | null; name: string } {
  const i = label.indexOf(' · ');
  if (i <= 0) return { code: null, name: label.trim() };
  return { code: label.slice(0, i).trim(), name: label.slice(i + 3).trim() };
}

/** Подсказка к бейджу кода части: откуда взят код (`raw.sheet.source`). */
export function partSourceHint(source: string | null): string | undefined {
  switch (source) {
    case 'text':
      return 'Код части — с основной надписи листа';
    case 'inherited':
      return 'Код части унаследован: в основной надписи листа кода нет, соседние листы — этой части';
    case 'kind':
      return 'Код с основной надписи не прочитан — часть определена по виду таблицы';
    default:
      return undefined;
  }
}

/** Подсказка к значку расхождения шифра: текст расхождения с заглавной буквы. */
export function mismatchHint(mismatch: string): string {
  const m = mismatch.trim();
  return m.charAt(0).toUpperCase() + m.slice(1);
}

/** Позиция ИИ (текстовое извлечение M2.1): часть «ИИ»; у позиции без части —
 *  по роли источника. У ИИ прежний `section` — узел или система. */
export function isAiPosition(p: Pick<PtoVolumePosition, 'doc_part' | 'source_role'>): boolean {
  const part = p.doc_part?.trim();
  return part ? part === 'ИИ' : p.source_role === 'rd';
}

// ── Место в РД («Источник») ──

/** Место строки в РД: «СП01, л. 2 (стр. PDF 56)», если с основной надписи
 *  известны код части и номер листа; иначе «стр. PDF 56». В тексте ссылки
 *  (`form = 'link'`) — без запятой: «СП01 л. 2 (стр. PDF 56)». Пусто — страница
 *  неизвестна. */
export function sourcePlace(
  p: Pick<PtoVolumePosition, 'page_number' | 'raw'>,
  form: 'cell' | 'link' = 'cell',
): string {
  const sheet = volumeSheet(p.raw);
  const page = p.page_number > 0 ? p.page_number : (sheet?.page ?? 0);
  const pdf = page > 0 ? `стр. PDF ${page}` : '';
  if (sheet?.code && sheet.no) {
    const at = `${sheet.code}${form === 'cell' ? ',' : ''} л. ${sheet.no}`;
    return pdf ? `${at} (${pdf})` : at;
  }
  return pdf;
}

// ── «Скопировать ссылку» ──

/** Поля позиции для ссылки на строку. */
export type LinkPosition = RowNumbered &
  Pick<PtoVolumePosition, 'record_uid' | 'name' | 'page_number' | 'raw'>;

/** Текст ссылки: «<шифр>, СП01 №5 — <наименование>, СП01 л. 2 (стр. PDF 56)»; у
 *  позиции ИИ — «<шифр>, ИИ №3 — <наименование>, ЧТЖ05 л. 4 (стр. PDF 58)».
 *  Без номера — «<шифр>, <наименование>, …»; шифр не загружен — без него. */
export function positionLinkText(p: LinkPosition, docCode: string | null): string {
  const label = rowLabel(p);
  const name = p.name.replace(/\s+/g, ' ').trim();
  return [docCode?.trim(), label ? `${label} — ${name}` : name, sourcePlace(p, 'link')]
    .filter(Boolean)
    .join(', ');
}

/** Код части в якоре: URL-кодировка, точка тоже кодируется — она отделяет часть
 *  от номера. */
function encodePart(part: string): string {
  return encodeURIComponent(part).replace(/\./g, '%2E');
}

/** Якорь строки: `pos=<часть>.<№>[.<подномер>]&uid=<record_uid>`; у строки без
 *  номера — только `uid=…`. */
export function positionAnchor(p: RowNumbered & Pick<PtoVolumePosition, 'record_uid'>): string {
  const out: string[] = [];
  const part = p.doc_part?.trim();
  if (part && typeof p.row_no === 'number') {
    const sub = p.row_no_sub ?? 0;
    out.push(`pos=${encodePart(part)}.${p.row_no}${sub > 0 ? `.${sub}` : ''}`);
  }
  if (p.record_uid) out.push(`uid=${encodeURIComponent(p.record_uid)}`);
  return out.join('&');
}

/** Адрес строки: `<origin>/agents/pto/<проект>/volumes/<ревизия>#<якорь>`. */
export function positionLinkUrl(
  origin: string,
  projectId: string,
  revisionId: string,
  p: RowNumbered & Pick<PtoVolumePosition, 'record_uid'>,
): string {
  const base = `${origin.replace(/\/+$/, '')}/agents/pto/${encodeURIComponent(projectId)}/volumes/${encodeURIComponent(revisionId)}`;
  const anchor = positionAnchor(p);
  return anchor ? `${base}#${anchor}` : base;
}

// ── Открытие по ссылке ──

/** Якорь ссылки на строку. */
export interface PositionAnchor {
  /** Часть и номер из `pos=`; null — номера в якоре нет. */
  part: string | null;
  rowNo: number | null;
  rowNoSub: number;
  /** `record_uid` строки — запасной ключ; null — нет. */
  uid: string | null;
}

function decode(s: string): string | null {
  try {
    return decodeURIComponent(s);
  } catch {
    return null;
  }
}

/** `<часть>.<№>[.<подномер>]` → часть и номер; не разобрали — null. Делим до
 *  декодирования: точка в коде части закодирована. */
function parsePos(value: string): Pick<PositionAnchor, 'part' | 'rowNo' | 'rowNoSub'> | null {
  const bits = value.split('.');
  if (bits.length < 2 || bits.length > 3) return null;
  const part = decode(bits[0])?.trim();
  const [, no, sub] = bits;
  if (!part || !/^\d+$/.test(no) || (sub !== undefined && !/^\d+$/.test(sub))) return null;
  return { part, rowNo: Number(no), rowNoSub: sub === undefined ? 0 : Number(sub) };
}

/** Разбор якоря `#pos=СП01.5.1&uid=…` (`location.hash`); код части — в
 *  URL-кодировке или как есть. Нет ни номера, ни `uid` — null. */
export function parsePositionAnchor(hash: string): PositionAnchor | null {
  const body = hash.startsWith('#') ? hash.slice(1) : hash;
  const out: PositionAnchor = { part: null, rowNo: null, rowNoSub: 0, uid: null };
  for (const pair of body.split('&')) {
    const eq = pair.indexOf('=');
    if (eq < 0) continue;
    const key = pair.slice(0, eq);
    const value = pair.slice(eq + 1);
    if (key === 'pos') {
      const pos = parsePos(value);
      if (pos) Object.assign(out, pos);
    } else if (key === 'uid') {
      out.uid = decode(value)?.trim() || null;
    }
  }
  return out.part !== null || out.uid !== null ? out : null;
}

/** Поля позиции для поиска по якорю. */
export type AnchorTarget = RowNumbered & Pick<PtoVolumePosition, 'record_uid'>;

/** Найденная по якорю строка и ключ, по которому нашли. */
export interface AnchorMatch<T extends AnchorTarget> {
  position: T;
  by: 'number' | 'uid';
}

/**
 * Строка по якорю — в ПОЛНОМ списке ревизии (фильтр «Только на проверку» не
 * важен). Сначала по номеру; не нашлась по номеру — по `record_uid`. Номер до
 * закрепления (выпуск 3) пересчитывает «Обновить разделы», поэтому если номер из
 * ссылки теперь у строки с другим `record_uid`, а строка ссылки в ревизии есть, —
 * берём её. Не нашлась ни так, ни так — null.
 */
export function findAnchoredPosition<T extends AnchorTarget>(
  items: readonly T[],
  a: PositionAnchor,
): AnchorMatch<T> | null {
  const byNo =
    a.part !== null && a.rowNo !== null
      ? items.find(
          (p) =>
            p.doc_part?.trim() === a.part &&
            p.row_no === a.rowNo &&
            (p.row_no_sub ?? 0) === a.rowNoSub,
        )
      : undefined;
  if (byNo && (!a.uid || byNo.record_uid === a.uid)) return { position: byNo, by: 'number' };
  const byUid = a.uid ? items.find((p) => p.record_uid === a.uid) : undefined;
  if (byUid) return { position: byUid, by: 'uid' };
  return byNo ? { position: byNo, by: 'number' } : null;
}

/** Пояснение над таблицей к открытию по ссылке: строка не найдена или её номер
 *  сменился; null — пояснять нечего. */
export function anchorNote(
  a: PositionAnchor,
  found: AnchorMatch<AnchorTarget> | null,
): string | null {
  const linked =
    a.part !== null && a.rowNo !== null ? partRowLabel(a.part, a.rowNo, a.rowNoSub) : null;
  if (!found) {
    return `Строка из ссылки${linked ? ` (${linked})` : ''} в этой ревизии не найдена: возможно, её убрала переобработка РД.`;
  }
  if (found.by === 'uid' && linked) {
    const now = rowLabel(found.position);
    if (now !== linked) {
      return `Номер строки из ссылки (${linked}) сменился: сейчас ${now ?? 'строка без номера'}.`;
    }
  }
  return null;
}

/** id строки таблицы в DOM — для прокрутки к строке по ссылке. */
export function volumeRowDomId(positionId: string): string {
  return `pto-volume-${positionId}`;
}
